"use client";

import { IconChevronDownMedium } from "@central-icons-react/round-outlined-radius-3-stroke-2";
import { format, isValid, parse } from "date-fns";
import { MonthCaption as DefaultMonthCaption, MonthGrid as DefaultMonthGrid } from "react-day-picker";
import * as React from "react";

import { Calendar } from "@/components/ui/calendar";
import {
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuSection,
} from "@/components/ui/popover-menu";
import { cn } from "@/lib/utils";

type Props = {
  /** `yyyy-MM-dd` in local time. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  triggerClassName?: string;
  rowClassName?: string;
};

function PopoverMenuEdgeDivider() {
  return (
    <div
      aria-hidden
      role="separator"
      className="my-1 h-px w-full shrink-0 bg-[#eee]"
    />
  );
}

/**
 * Date picker that uses the shared PopoverMenu surface + Calendar grid.
 * Prefer this over native `showPicker()` when the control lives in product UI.
 */
export function PopoverMenuDatePicker({
  value,
  onChange,
  disabled = false,
  ariaLabel = "Pick date",
  triggerClassName,
  rowClassName,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date>(() => new Date());
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  const selected = isValid(parsed) ? parsed : undefined;
  const today = new Date();
  const isTodaySelected =
    selected != null &&
    format(selected, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
  const label = isTodaySelected
    ? "Today"
    : selected
      ? format(selected, "MMM d, yyyy")
      : value;

  const selectToday = () => {
    onChange(format(today, "yyyy-MM-dd"));
    setMonth(today);
    setOpen(false);
  };

  return (
    <PopoverMenu
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        if (next) setMonth(selected ?? today);
        setOpen(next);
      }}
      rowClassName={cn("min-w-0", rowClassName)}
      menuContentClassName="w-auto p-0"
      trigger={
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={triggerClassName}
        >
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          <IconChevronDownMedium
            className="size-3 shrink-0 text-neutral-07"
            aria-hidden
          />
        </button>
      }
      menu={
        <>
          <PopoverMenuSection className="p-0">
            <Calendar
              mode="single"
              month={month}
              onMonthChange={setMonth}
              selected={selected}
              onSelect={(day) => {
                if (!day) return;
                onChange(format(day, "yyyy-MM-dd"));
                setOpen(false);
              }}
              className="bg-transparent px-0 pt-1 pb-0"
              classNames={{
                month: "flex w-full flex-col gap-0",
                month_caption:
                  "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
              }}
              components={{
                MonthCaption: (props) => (
                  <div className="px-1">
                    <DefaultMonthCaption {...props} />
                  </div>
                ),
                MonthGrid: (props) => (
                  <>
                    <PopoverMenuEdgeDivider />
                    <div className="px-1 py-1">
                      <DefaultMonthGrid {...props} />
                    </div>
                  </>
                ),
              }}
            />
          </PopoverMenuSection>
          <PopoverMenuEdgeDivider />
          <PopoverMenuSection className="px-1 pb-1">
            <PopoverMenuItem
              label="Today"
              selected={isTodaySelected}
              disabled={disabled}
              onClick={selectToday}
              className="relative justify-center text-center [&>span:first-child]:flex-none [&>span:first-child]:text-center [&>span:nth-child(2)]:absolute [&>span:nth-child(2)]:right-2"
            />
          </PopoverMenuSection>
        </>
      }
    />
  );
}
